/**
 * Non-blocking Analytics Wrapper
 * Ensures all analytics operations are truly non-blocking and performant
 */

import { analytics as proxyAnalytics } from './analytics-proxy';

/**
 * Request idle callback with fallback for browsers that don't support it
 */
const requestIdleCallback =
    (typeof window !== 'undefined' && window.requestIdleCallback) ||
    ((cb: () => void) => setTimeout(cb, 1));

/**
 * Wraps an analytics function to ensure it never blocks the main thread
 * Uses requestIdleCallback to defer execution when the browser is idle
 */
function makeNonBlocking<T extends (...args: any[]) => any>(
    fn: T
): (...args: Parameters<T>) => void {
    return (...args: Parameters<T>) => {
        // Defer to idle time or next tick
        requestIdleCallback(() => {
            try {
                fn(...args);
            } catch (error) {
                // Silently catch errors - analytics should never break the app
                console.debug(
                    '[Analytics] Non-blocking wrapper caught error:',
                    error
                );
            }
        });
    };
}

/**
 * Performance-safe analytics with guaranteed non-blocking behavior
 */
export const performantAnalytics = {
    /**
     * Capture event - guaranteed non-blocking
     * Deferred to idle time for zero performance impact
     */
    capture: makeNonBlocking(proxyAnalytics.capture),

    /**
     * Identify user - guaranteed non-blocking
     * Deferred to idle time for zero performance impact
     */
    identify: makeNonBlocking(proxyAnalytics.identify),

    /**
     * Track page view - guaranteed non-blocking
     * Deferred to idle time for zero performance impact
     */
    pageView: makeNonBlocking(proxyAnalytics.pageView),

    /**
     * Check if blocked - synchronous, returns cached result
     */
    checkBlocked: proxyAnalytics.checkBlocked,
};

/**
 * Batch analytics events for even better performance
 * Collects events and sends them in batches during idle time
 */
class BatchedAnalytics {
    private eventQueue: Array<{
        type: 'capture' | 'identify' | 'pageView';
        args: any[];
        timestamp: number;
    }> = [];

    private flushTimeout: NodeJS.Timeout | null = null;
    private readonly maxBatchSize = 10;
    private readonly maxBatchDelay = 1000; // 1 second max delay

    capture(eventName: string, properties?: Record<string, any>): void {
        this.addToQueue('capture', [eventName, properties]);
    }

    identify(userId: string, properties?: Record<string, any>): void {
        this.addToQueue('identify', [userId, properties]);
    }

    pageView(url?: string): void {
        this.addToQueue('pageView', [url]);
    }

    private addToQueue(
        type: 'capture' | 'identify' | 'pageView',
        args: any[]
    ): void {
        this.eventQueue.push({
            type,
            args,
            timestamp: Date.now(),
        });

        // Flush if batch is full
        if (this.eventQueue.length >= this.maxBatchSize) {
            this.flush();
        } else {
            // Schedule flush if not already scheduled
            this.scheduleFlush();
        }
    }

    private scheduleFlush(): void {
        if (this.flushTimeout) return;

        this.flushTimeout = setTimeout(() => {
            this.flush();
        }, this.maxBatchDelay);
    }

    private flush(): void {
        if (this.flushTimeout) {
            clearTimeout(this.flushTimeout);
            this.flushTimeout = null;
        }

        if (this.eventQueue.length === 0) return;

        // Process queue in idle time
        const eventsToProcess = [...this.eventQueue];
        this.eventQueue = [];

        requestIdleCallback(() => {
            eventsToProcess.forEach((event) => {
                try {
                    switch (event.type) {
                        case 'capture':
                            proxyAnalytics.capture(
                                event.args[0],
                                event.args[1]
                            );
                            break;
                        case 'identify':
                            proxyAnalytics.identify(
                                event.args[0],
                                event.args[1]
                            );
                            break;
                        case 'pageView':
                            proxyAnalytics.pageView(event.args[0]);
                            break;
                    }
                } catch (error) {
                    console.debug(
                        '[BatchedAnalytics] Error processing event:',
                        error
                    );
                }
            });
        });
    }
}

// Export batched analytics instance
export const batchedAnalytics = new BatchedAnalytics();

// Export the most performant option as default
export const analytics = performantAnalytics;
